import React, { useState, useEffect, useRef } from "react";
import { Row } from "reactstrap";
import { useFormatEngagementMetricsMutation } from "../../../store/api/analyticsApi";

const MetricSummaryRow = ({ metrics }) => {
  const [formatEngagementMetrics] = useFormatEngagementMetricsMutation();
  const [formattedMetrics, setFormattedMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formattedTextsRequested, setFormattedTextsRequested] = useState(false);
  
  const prevMetricsRef = useRef(null);
  const metricsToFormatRef = useRef([]);

  const getPreviousPeriodText = (period) => {
    if (!period) return "the previous period";
    const p = (period || "").toString().toLowerCase();
    if (p === "daily" || p.includes("day")) return "the previous day";
    if (p.includes("7") || p.includes("week")) return "the previous 7 days";
    if (p.includes("12") || p.includes("12 months") || p.includes("last 12") || p.includes("12-month")) return "the previous year";
    if (p.includes("year")) return "the previous year";
    if (p.includes("quarter")) return "the previous quarter";
    if (p.includes("month")) return "the previous month";
      return "the previous period";
  };
  
  useEffect(() => {
    if (!metrics || metrics.length === 0) return;
    
    const metricsChanged = JSON.stringify(metrics) !== JSON.stringify(prevMetricsRef.current);
    
    if (metricsChanged) {
      prevMetricsRef.current = JSON.parse(JSON.stringify(metrics));
      setFormattedTextsRequested(false);
    }
  }, [metrics]);
    useEffect(() => {
    const formatMetrics = async () => {
      if (!metrics || metrics.length === 0 || formattedTextsRequested || loading) return;
      
      const regularMetrics = metrics.filter(metric => 
        !metric.isAI && 
        metric.value !== undefined && 
        metric.value !== null && 
        metric.percentageChange !== undefined &&
        metric.percentageChange !== null
      );if (regularMetrics.length === 0) return;
      
      const hasValidMetrics = regularMetrics.every(metric => 
        metric.label && 
        metric.value !== undefined && 
        metric.value !== null &&
        metric.value !== 0 &&
        metric.percentageChange !== undefined &&
        metric.period
      );
      
      if (!hasValidMetrics) {
        return;
      }

      const allMetricsHaveRealData = regularMetrics.every(metric => {
        const valueIsReal = typeof metric.value === 'string' ? 
          metric.value !== '0' && metric.value !== '0.00' : 
          metric.value > 0;
            
        const percentageIsReal = 
          metric.percentageChange !== 0 && 
          metric.percentageChange !== 0.00;
            
        return valueIsReal && percentageIsReal;
      });
      
      if (!allMetricsHaveRealData) {
        return;
      }
      
      const metricsToFormat = regularMetrics.map(metric => {
        const prevPeriodText = getPreviousPeriodText(metric.period);
        return `${metric.label} is ${metric.value}, ${Math.abs(metric.percentageChange).toFixed(1).replace(/\.0$/, "")} % ${metric.percentageChange >= 0 ? "more" : "less"} than ${prevPeriodText}`;
      });

      if (metricsToFormat.length === 0) {
        return;
      }
      
      const previousMetrics = metricsToFormatRef.current;
      const hasSignificantChange = !previousMetrics.length || 
        metricsToFormat.some((metric, index) => {
          if (!previousMetrics[index]) return true;
          const prevMetricText = previousMetrics[index];
          const metricText = metric;
          return prevMetricText !== metricText;
        });
      
      metricsToFormatRef.current = [...metricsToFormat];
      
      if (!hasSignificantChange) {
        return;
      }
      
      setFormattedTextsRequested(true);
      setLoading(true);
            
      try {
        const response = await formatEngagementMetrics({ text_array: metricsToFormat }).unwrap();
        if (response && response.formatted_texts && 
            Array.isArray(response.formatted_texts) && 
            response.formatted_texts.some(text => text && text.trim() !== "")) {
          const validFormattedTexts = response.formatted_texts.map(text => 
            text && text.trim() !== "" ? text : null
          );
          setFormattedMetrics(validFormattedTexts);
        } else {
          console.log("Received empty or invalid formatted texts from API");
        }
      } catch (error) {
        console.error("Error formatting metrics:", error);
      } finally {
        setLoading(false);
      }
    };
    
    formatMetrics();
  }, [metrics, formattedTextsRequested, loading, formattedMetrics.length, formatEngagementMetrics]);
  
  return (
    <Row className="align-items-start mx-3 my-4 flex-column">
      {loading ? (
        <p className="metrics mb-0 fs-2">Loading metrics...</p>
      ) : (
        metrics.map((metric, index) => {
          if (metric.isAI) {
            if (metric.insight) {
              return (
                <p key={index} className="metrics mb-0 fs-2 ai-insight">
                  <i className="fas fa-robot me-2"></i> {metric.insight.charAt(0).toUpperCase() + metric.insight.slice(1)}
                </p>
              );
            } else if (metric.label === "Active Users") {
              return (
                <p key={index} className="metrics mb-0 fs-2">
                  {metric.label} is {metric.value}
                </p>
              );
            }
            const periodText = metric.period ? (metric.period === "Daily" ? "in the last day" : `in the ${metric.period.toLowerCase()}`) : "";
            return (
              <p key={index} className="metrics mb-0 fs-2">
                {metric.label} is {metric.value} of total {metric.metric_type} {periodText}
              </p>
            );
          }
          const regularMetricsBeforeThisOne = metrics.slice(0, index).filter(m => !m.isAI).length;
          const formattedMetric = formattedMetrics && formattedMetrics.length > regularMetricsBeforeThisOne ? formattedMetrics[regularMetricsBeforeThisOne] : null;
          const hasRealValue = metric.value && (typeof metric.value === 'string' ? metric.value !== '0' && metric.value !== '0.00' : metric.value > 0);
          const defaultText = `${metric.label} is ${metric.value}, ${Math.abs(metric.percentageChange).toFixed(1)}% ${metric.percentageChange >= 0 ? "more" : "less"} than ${getPreviousPeriodText(metric.period)}`;
          let htmlContent = (formattedMetric && hasRealValue) ? formattedMetric : defaultText;
          if (htmlContent && typeof htmlContent === 'string') {
            htmlContent = htmlContent.charAt(0).toUpperCase() + htmlContent.slice(1);
          }
          return (
            <p 
              key={index} 
              id="metricText" 
              className="metrics mb-0 fs-2"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          );
        })
      )}
    </Row>
  );
};

export default MetricSummaryRow;
